import { useState, useMemo } from "react";

// Constantes normativas 2026 (Perú)
const UIT_2026 = 5500;
const UMBRAL_RETENCION_RECIBO = 1500;
const TASA_RETENCION = 0.08;
const DEDUCCION_PRESUNTA = 0.20;
const TOPE_DEDUCCION_UIT = 24;
const DEDUCCION_ADICIONAL_UIT = 7;
const UMBRAL_SUSPENSION_PROFESIONAL = 48125;
const UMBRAL_OBLIGACION_DECLARAR = 7 * UIT_2026;

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const TRAMOS_RENTA = [
  { limite: 5 * UIT_2026, tasa: 0.08 },
  { limite: 20 * UIT_2026, tasa: 0.14 },
  { limite: 35 * UIT_2026, tasa: 0.17 },
  { limite: 45 * UIT_2026, tasa: 0.20 },
  { limite: Infinity, tasa: 0.30 },
];

function calcularImpuestoAnual(ingresoAnualAfecto) {
  let restante = Math.max(0, ingresoAnualAfecto);
  let anterior = 0;
  let impuesto = 0;
  for (const tramo of TRAMOS_RENTA) {
    if (restante <= 0) break;
    const anchoTramo = tramo.limite - anterior;
    const montoEnTramo = Math.min(restante, anchoTramo);
    impuesto += montoEnTramo * tramo.tasa;
    restante -= montoEnTramo;
    anterior = tramo.limite;
  }
  return impuesto;
}

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function Calculadora4taCategoria() {
  const [montoRecibo, setMontoRecibo] = useState(3000);
  const [tipoContribuyente, setTipoContribuyente] = useState("independiente");
  const [tieneSuspension, setTieneSuspension] = useState(false);

  const [mostrarAvanzado, setMostrarAvanzado] = useState(false);
  const [mesRecibo, setMesRecibo] = useState("Agosto");
  const [ingresoAcumulado4ta, setIngresoAcumulado4ta] = useState(0);
  const [retencionesAcumuladas, setRetencionesAcumuladas] = useState(0);
  const [ingresoAnual5ta, setIngresoAnual5ta] = useState(0);

  const resultadoRecibo = useMemo(() => {
    const monto = Math.max(0, Number(montoRecibo) || 0);
    const aplicaRetencion = !tieneSuspension && monto > UMBRAL_RETENCION_RECIBO;
    const retencion = aplicaRetencion ? monto * TASA_RETENCION : 0;
    const neto = monto - retencion;
    return { monto, aplicaRetencion, retencion, neto };
  }, [montoRecibo, tieneSuspension]);

  const resultadoAnual = useMemo(() => {
    const acumulado4ta = Math.max(0, Number(ingresoAcumulado4ta) || 0);
    const acumuladoRetenido = Math.max(0, Number(retencionesAcumuladas) || 0);
    const ingreso5ta = Math.max(0, Number(ingresoAnual5ta) || 0);

    const ingreso4ta = acumulado4ta + resultadoRecibo.monto;
    const retenido = acumuladoRetenido + resultadoRecibo.retencion;

    const esIndependiente = tipoContribuyente === "independiente";
    const topeDeduccion = TOPE_DEDUCCION_UIT * UIT_2026;
    const deduccion20 = esIndependiente
      ? Math.min(ingreso4ta * DEDUCCION_PRESUNTA, topeDeduccion)
      : 0;
    const rentaNeta4ta = ingreso4ta - deduccion20;

    const rentaNetaTotal = rentaNeta4ta + ingreso5ta;
    const ingresoAfecto = rentaNetaTotal - DEDUCCION_ADICIONAL_UIT * UIT_2026;

    const impuestoAnual = calcularImpuestoAnual(ingresoAfecto);
    const saldo = retenido - impuestoAnual; 

    const obligadoADeclarar = ingreso4ta + ingreso5ta > UMBRAL_OBLIGACION_DECLARAR || saldo !== 0;
    const puedeSolicitarSuspension = esIndependiente && ingreso4ta <= UMBRAL_SUSPENSION_PROFESIONAL;

    return {
      esIndependiente,
      ingreso4ta,
      ingreso5ta,
      deduccion20,
      rentaNeta4ta,
      rentaNetaTotal,
      ingresoAfecto,
      impuestoAnual,
      retenido,
      saldo,
      obligadoADeclarar,
      puedeSolicitarSuspension,
    };
  }, [ingresoAcumulado4ta, retencionesAcumuladas, ingresoAnual5ta, tipoContribuyente, resultadoRecibo]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";
  const labelClase = "block text-sm font-medium text-slate-700 mb-1.5";
  const opcionTipoClase = (activo) =>
    `flex-1 rounded-lg border py-2.5 px-3 text-sm font-medium text-left transition-colors cursor-pointer ${
      activo
        ? "border-red-700 bg-red-50 text-red-800"
        : "border-slate-300 text-slate-600 hover:bg-slate-50 bg-white"
    }`;

  const hayDatosAvanzados =
    Number(ingresoAcumulado4ta) > 0 ||
    Number(retencionesAcumuladas) > 0 ||
    Number(ingresoAnual5ta) > 0;

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            💳
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora 4ta Categoría
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Recibos por honorarios y declaración anual</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className={labelClase}>Monto del Recibo por Honorarios (S/)</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={montoRecibo}
                  onChange={(e) => setMontoRecibo(e.target.value)}
                  className={`${inputClase} pl-9`}
                />
              </div>
            </div>

            <div>
              <label className={labelClase}>Tipo de contribuyente</label>
              <div className="flex gap-2">
                <label className={opcionTipoClase(tipoContribuyente === "independiente")}>
                  <input
                    type="radio"
                    name="tipoContribuyente"
                    className="sr-only"
                    checked={tipoContribuyente === "independiente"}
                    onChange={() => setTipoContribuyente("independiente")}
                  />
                  Independiente
                </label>
                <label className={opcionTipoClase(tipoContribuyente === "director")}>
                  <input
                    type="radio"
                    name="tipoContribuyente"
                    className="sr-only"
                    checked={tipoContribuyente === "director"}
                    onChange={() => setTipoContribuyente("director")}
                  />
                  Director / Síndico
                </label>
              </div>
              {tipoContribuyente === "director" && (
                <p className="mt-1.5 text-xs text-slate-400">
                  Las dietas de directores, síndicos, mandatarios, gestores de negocios y
                  albaceas no acceden a la deducción presunta del 20%.
                </p>
              )}
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tieneSuspension}
                onChange={(e) => setTieneSuspension(e.target.checked)}
                className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
              />
              <span className="text-sm text-slate-700">
                Tengo constancia de suspensión de retenciones vigente
              </span>
            </label>

            <p className="p-3 text-xs rounded-lg text-slate-400 bg-slate-50">
              La retención del 8% aplica cuando el recibo supera S/{" "}
              {UMBRAL_RETENCION_RECIBO.toLocaleString("es-PE")}, salvo que tengas
              suspensión vigente. Esta sección calcula únicamente el recibo que estás emitiendo.
            </p>
          </div>

          <div className="border-b border-slate-100">
            <button
              type="button"
              onClick={() => setMostrarAvanzado((v) => !v)}
              className="flex items-center justify-between w-full px-6 py-4 text-sm font-medium text-left text-slate-700 hover:bg-slate-50"
            >
              <span className="flex items-center gap-2">
                {mostrarAvanzado ? "▼" : "▶"} Opciones avanzadas
                {!mostrarAvanzado && hayDatosAvanzados && (
                  <span className="w-1.5 h-1.5 rounded-full bg-red-700" />
                )}
              </span>
              <span className="text-xs font-normal text-slate-400">
                Proyección anual
              </span>
            </button>

            {mostrarAvanzado && (
              <div className="px-6 pb-6 space-y-5">
                <div>
                  <label className={labelClase}>Mes del recibo</label>
                  <select
                    value={mesRecibo}
                    onChange={(e) => setMesRecibo(e.target.value)}
                    className={inputClase}
                  >
                    {MESES.map((mes) => (
                      <option key={mes} value={mes}>
                        {mes}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClase}>Ingresos acumulados de 4ta categoría</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                      S/
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={ingresoAcumulado4ta}
                      onChange={(e) => setIngresoAcumulado4ta(e.target.value)}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    No incluyas el recibo de arriba: se suma automáticamente.
                  </p>
                </div>

                <div>
                  <label className={labelClase}>Retenciones acumuladas</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                      S/
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={retencionesAcumuladas}
                      onChange={(e) => setRetencionesAcumuladas(e.target.value)}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    Tampoco incluyas la retención del recibo de arriba.
                  </p>
                </div>

                <div>
                  <label className={labelClase}>Ingresos de 5ta categoría (opcional)</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                      S/
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={ingresoAnual5ta}
                      onChange={(e) => setIngresoAnual5ta(e.target.value)}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Monto neto a cobrar ({mesRecibo})
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(resultadoRecibo.neto)}
            </p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Monto del recibo</span>
                <span className="font-mono">{formatearSoles(resultadoRecibo.monto)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>
                  − Retención 8% {resultadoRecibo.aplicaRetencion ? "" : "(no aplica)"}
                </span>
                <span className="font-mono">−{formatearSoles(resultadoRecibo.retencion)}</span>
              </div>
            </div>
          </div>

          {hayDatosAvanzados && (
            <div className="p-6 border-t bg-slate-800 border-slate-700">
              <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
                {resultadoAnual.saldo >= 0 ? "Saldo a favor estimado (año)" : "Impuesto por pagar estimado (año)"}
              </p>
              <p className="mb-5 font-mono text-3xl font-bold text-white">
                {formatearSoles(Math.abs(resultadoAnual.saldo))}
              </p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Ingresos de 4ta (acumulado + este recibo)</span>
                  <span className="font-mono">{formatearSoles(resultadoAnual.ingreso4ta)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>
                    − Deducción presunta (20%){resultadoAnual.esIndependiente ? "" : " (no aplica)"}
                  </span>
                  <span className="font-mono">−{formatearSoles(resultadoAnual.deduccion20)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Renta neta de 4ta</span>
                  <span className="font-mono">{formatearSoles(resultadoAnual.rentaNeta4ta)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>− 7 UIT (deducción adicional compartida)</span>
                  <span className="font-mono">−{formatearSoles(DEDUCCION_ADICIONAL_UIT * UIT_2026)}</span>
                </div>
                <div className="flex justify-between pt-1.5 mt-1.5 text-slate-300 border-t border-slate-700">
                  <span>Impuesto anual calculado</span>
                  <span className="font-mono">{formatearSoles(resultadoAnual.impuestoAnual)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Retenciones totales (acumulado + este recibo)</span>
                  <span className="font-mono">{formatearSoles(resultadoAnual.retenido)}</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                {resultadoAnual.obligadoADeclarar
                  ? "Con estos montos, probablemente estás obligado a presentar la Declaración Anual."
                  : "Con estos montos, probablemente no estás obligado a presentar la Declaración Anual."}{" "}
                {resultadoAnual.puedeSolicitarSuspension &&
                  `Podrías calificar para solicitar la suspensión de retenciones (tope S/${UMBRAL_SUSPENSION_PROFESIONAL.toLocaleString("es-PE")}/año).`}
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 space-y-1 text-xs leading-relaxed text-slate-400">
          <p>1. Este cálculo es referencial y no constituye una declaración oficial ante SUNAT.</p>
          <p>
            2. La deducción del 20% es presunta (no requiere sustento de gastos), con
            tope de 24 UIT anuales, y solo aplica a rentas de trabajo independiente; las
            dietas de directores, síndicos, mandatarios, gestores de negocios y albaceas
            no acceden a ella. La deducción adicional de 7 UIT se comparte entre tus
            rentas de 4ta y 5ta categoría si tienes ambas.
          </p>
          <p>
            3. El umbral de retención por recibo (S/1,500), el límite para solicitar
            suspensión (S/48,125/año para profesionales independientes) y el umbral de
            declaración anual (7 UIT) se actualizan periódicamente por SUNAT.
          </p>
          <p>4. Para tu cálculo oficial, revisa tu Clave SOL en sunat.gob.pe. No constituye asesoría tributaria.</p>
        </div>
      </div>
    </div>
  );
}